import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, TextField, InputAdornment, Chip, Button,
    FormControl, InputLabel, Select, MenuItem, FormControlLabel,
    Switch, Collapse, Tooltip, CircularProgress, TablePagination,
    Menu, ListItemIcon, ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import HistoryIcon from '@mui/icons-material/History';

import api from '../services/api';
import { toastSuccess, toastError, confirmAcao } from '../services/alerts';

const TIPO_LABEL = { CORRENTE: 'Corrente', POUPANCA: 'Poupança', PAGAMENTO: 'Pagamento' };

export default function ContaBancaria() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [contas, setContas] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [busca, setBusca] = useState('');
    const [debouncedBusca, setDebouncedBusca] = useState('');
    const [tipoFilter, setTipoFilter] = useState('');
    const [ativoFilter, setAtivoFilter] = useState('');
    const [saldoMin, setSaldoMin] = useState('');
    const [saldoMax, setSaldoMax] = useState('');
    const [saldoNegativo, setSaldoNegativo] = useState(false);
    const [psicologoId, setPsicologoId] = useState('');
    const [semPsicologo, setSemPsicologo] = useState(false);
    const [chavePix, setChavePix] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [psicologos, setPsicologos] = useState([]);
    const [menuAnchor, setMenuAnchor] = useState({ el: null, conta: null });
    const abrirMenu = (e, conta) => setMenuAnchor({ el: e.currentTarget, conta });
    const fecharMenu = () => setMenuAnchor(prev => ({ ...prev, el: null }));

    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300);
        return () => clearTimeout(t);
    }, [busca]);

    useEffect(() => {
        api.get('/psicologos')
            .then(r => setPsicologos(Array.isArray(r.data) ? r.data : (r.data?.content ?? [])))
            .catch(() => {});
    }, []);

    const hasActiveFilters = !!(tipoFilter || ativoFilter !== '' || saldoMin || saldoMax ||
        saldoNegativo || psicologoId || semPsicologo || chavePix);

    const buildParams = useCallback(() => {
        const params = { page, size: rowsPerPage };
        if (debouncedBusca) params.busca = debouncedBusca;
        if (tipoFilter) params.tipoConta = tipoFilter;
        if (ativoFilter !== '') params.ativo = ativoFilter;
        if (saldoMin) params.saldoMin = saldoMin;
        if (saldoMax) params.saldoMax = saldoMax;
        if (saldoNegativo) params.saldoNegativo = true;
        if (psicologoId) params.psicologoId = psicologoId;
        if (semPsicologo) params.semPsicologo = true;
        if (chavePix) params.chavePix = chavePix;
        return params;
    }, [page, rowsPerPage, debouncedBusca, tipoFilter, ativoFilter, saldoMin, saldoMax,
        saldoNegativo, psicologoId, semPsicologo, chavePix]);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/contas-bancarias/paginado', { params: buildParams() });
            setContas(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => { carregarDados(); }, [carregarDados]);

    useEffect(() => { setPage(0); }, [debouncedBusca, tipoFilter, ativoFilter, saldoMin, saldoMax,
        saldoNegativo, psicologoId, semPsicologo, chavePix]);

    const handleCopiarPix = async (item) => {
        fecharMenu();
        if (!item.chavePix) { toastError('Conta sem chave PIX.'); return; }
        try {
            await navigator.clipboard.writeText(item.chavePix);
            toastSuccess('Chave PIX copiada');
        } catch {
            toastError('Não foi possível copiar.');
        }
    };

    const handleAlterarStatus = async (item) => {
        fecharMenu();
        const inativando = item.ativo;
        const ok = await confirmAcao({
            title: inativando ? 'Inativar conta' : 'Reativar conta',
            text: inativando
                ? 'A conta será inativada e ocultada de seleções padrão, mas o histórico será mantido. Continuar?'
                : 'Reativar esta conta bancária?',
            confirmButtonText: inativando ? 'Inativar' : 'Reativar',
            icon: inativando ? 'warning' : 'question',
            danger: inativando,
        });
        if (!ok) return;
        try {
            await api.patch(`/contas-bancarias/${item.id_c_bancaria}/${inativando ? 'inativar' : 'reativar'}`);
            toastSuccess(inativando ? 'Conta inativada' : 'Conta reativada');
            carregarDados();
        } catch (e) {
            console.error(e);
        }
    };

    const handleLimparFiltros = () => {
        setTipoFilter('');
        setAtivoFilter('');
        setSaldoMin('');
        setSaldoMax('');
        setSaldoNegativo(false);
        setPsicologoId('');
        setSemPsicologo(false);
        setChavePix('');
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Contas Bancárias
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Histórico de alterações">
                        <IconButton onClick={() => navigate('/financeiro/historico?tab=2')}
                            sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/conta-bancaria/nova')}>
                        Nova Conta
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Buscar por banco, agência, conta ou PIX..."
                        variant="outlined"
                        fullWidth
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                            endAdornment: busca && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setBusca('')} edge="end"><ClearIcon /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Filtros avançados">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2,
                                    p: 1.2,
                                    bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {hasActiveFilters && !showFilters && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Tipo de Conta</InputLabel>
                            <Select value={tipoFilter} label="Tipo de Conta" onChange={(e) => setTipoFilter(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="CORRENTE">Corrente</MenuItem>
                                <MenuItem value="POUPANCA">Poupança</MenuItem>
                                <MenuItem value="PAGAMENTO">Pagamento</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={ativoFilter} label="Status" onChange={(e) => setAtivoFilter(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value={true}>Ativas</MenuItem>
                                <MenuItem value={false}>Inativas</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Psicólogo</InputLabel>
                            <Select value={psicologoId} label="Psicólogo" onChange={(e) => setPsicologoId(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                {psicologos.map(p => (
                                    <MenuItem key={p.id_psicologo} value={p.id_psicologo}>{p.nomeCompleto}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Chave PIX"
                            size="small"
                            value={chavePix}
                            onChange={(e) => setChavePix(e.target.value)}
                            sx={{ minWidth: 160, bgcolor: '#fff' }}
                        />

                        <TextField
                            label="Saldo Mínimo (R$)"
                            size="small"
                            type="number"
                            value={saldoMin}
                            onChange={(e) => setSaldoMin(e.target.value)}
                            sx={{ minWidth: 140, bgcolor: '#fff' }}
                        />
                        <TextField
                            label="Saldo Máximo (R$)"
                            size="small"
                            type="number"
                            value={saldoMax}
                            onChange={(e) => setSaldoMax(e.target.value)}
                            sx={{ minWidth: 140, bgcolor: '#fff' }}
                        />

                        <FormControlLabel
                            control={<Switch checked={saldoNegativo} onChange={(e) => setSaldoNegativo(e.target.checked)} size="small" />}
                            label="Saldo negativo"
                        />
                        <FormControlLabel
                            control={<Switch checked={semPsicologo} onChange={(e) => setSemPsicologo(e.target.checked)} size="small" />}
                            label="Sem psicólogo"
                        />

                        {hasActiveFilters && (
                            <Button variant="text" color="error" size="small" onClick={handleLimparFiltros} sx={{ fontWeight: 'bold' }}>
                                Limpar filtros
                            </Button>
                        )}
                    </Box>
                </Collapse>

                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
                    {totalElements} registro(s){(debouncedBusca || hasActiveFilters) && ' • Filtros aplicados'}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                    </Box>
                ) : (
                <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Banco</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Agência / Conta</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Tipo</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Psicólogo</strong></TableCell>
                                <TableCell><strong>Saldo Atual</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contas.length > 0 ? contas.map((item) => {
                                const saldo = Number(item.saldoAtual || 0);
                                return (
                                    <TableRow key={item.id_c_bancaria} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccountBalanceIcon fontSize="small" color="action" />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.banco}</Typography>
                                                    {isMobile && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Ag. {item.agencia} / C. {item.numeroConta}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <Typography variant="body2">Ag. {item.agencia}</Typography>
                                            <Typography variant="caption" color="text.secondary">C. {item.numeroConta}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {item.tipoConta ? TIPO_LABEL[item.tipoConta] || item.tipoConta : '-'}
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {item.nomePsicologo || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {saldo >= 0
                                                    ? <TrendingUpIcon fontSize="small" color="success" />
                                                    : <TrendingDownIcon fontSize="small" color="error" />}
                                                <Typography sx={{ fontWeight: 'bold', color: saldo >= 0 ? 'success.main' : 'error.main', fontSize: '0.9rem' }}>
                                                    R$ {saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <Chip label={item.ativo ? 'Ativa' : 'Inativa'} color={item.ativo ? 'success' : 'default'} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => abrirMenu(e, item)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            Nenhum registro encontrado.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                )}

                <Menu anchorEl={menuAnchor.el} open={Boolean(menuAnchor.el)} onClose={fecharMenu}>
                    <MenuItem onClick={() => { fecharMenu(); navigate(`/conta-bancaria/${menuAnchor.conta?.id_c_bancaria}`); }}>
                        <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Editar</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleCopiarPix(menuAnchor.conta)} disabled={!menuAnchor.conta?.chavePix}>
                        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Copiar chave PIX</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleAlterarStatus(menuAnchor.conta)}>
                        <ListItemIcon>
                            {menuAnchor.conta?.ativo
                                ? <BlockIcon fontSize="small" color="warning" />
                                : <CheckCircleIcon fontSize="small" color="success" />}
                        </ListItemIcon>
                        <ListItemText>{menuAnchor.conta?.ativo ? 'Inativar' : 'Reativar'}</ListItemText>
                    </MenuItem>
                </Menu>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>
        </Box>
    );
}
